import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  Building2, FileText, Clock, CheckCircle, XCircle, AlertCircle, MessageSquare,
  Award, Send, Download, Upload, Users, CreditCard, TrendingUp, DollarSign,
  FileUp, Eye, Percent, BarChart2, PieChart
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell } from 'recharts';
import { PaymentButton } from '@/components/PaymentButton';
import { FeesPaymentsSection } from '@/components/FeesPaymentsSection';
import MessagesTab from '@/components/MessagesTab';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { 
  fetchFeeStructure, 
  getExamFee as feeServiceGetExamFee, 
  getInstitutionDiscountedFee,
  getExaminationTypes,
  getDefaultFees,
  DISCOUNT_RATES 
} from '@/utils/feeService';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#22c55e', '#ef4444', '#f59e0b', '#6366f1'];

// Get default examinations for immediate display (will be updated from backend)
const getInitialExaminations = () => getExaminationTypes(getDefaultFees());

// Get fee for examination using fee service
const getExamFee = (examCode, feeStructure = null) => feeServiceGetExamFee(examCode, feeStructure);

// Student Nomination Tab Component
function StudentNominationTab({ token, institutionName }) {
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedNomination, setSelectedNomination] = useState(null);
  const [examinations, setExaminations] = useState(getInitialExaminations());
  const [feeStructure, setFeeStructure] = useState(null);
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', date_of_birth: '', gender: '',
    nationality: 'India', qualification: '', medical_reg_number: '',
    examination: '', route: '1', specialty: '', years_of_practice: ''
  });

  const fetchNominations = async () => {
    try {
      const res = await axios.get(`${API}/accreditation/student-nominations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominations(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  // Fetch examination fees from backend
  const fetchExamFees = async () => {
    try {
      const fees = await fetchFeeStructure();
      setFeeStructure(fees);
      setExaminations(getExaminationTypes(fees));
    } catch (err) { console.error('Failed to fetch fee structure', err); }
  };

  useEffect(() => { 
    fetchNominations(); 
    fetchExamFees();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const handlePayFee = (nomination) => {
    setSelectedNomination(nomination);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (paymentId, nomination) => {
    try {
      await axios.post(`${API}/accreditation/student-nominations/${nomination.id}/payment`, {
        payment_id: paymentId,
        amount: Math.round(getExamFee(nomination.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION))
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment successful! Receipt sent to student email.');
      setShowPaymentModal(false);
      setSelectedNomination(null);
      fetchNominations();
    } catch (err) {
      toast.error('Payment recorded but failed to update. Please contact support.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.examination || !form.date_of_birth) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/accreditation/student-nominations`, {
        ...form,
        years_of_practice: parseInt(form.years_of_practice) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Student nominated successfully! Application submitted for admin approval.');
      setShowForm(false);
      setForm({
        full_name: '', email: '', phone: '', date_of_birth: '', gender: '',
        nationality: 'India', qualification: '', medical_reg_number: '',
        examination: '', route: '1', specialty: '', years_of_practice: ''
      });
      fetchNominations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to nominate student');
    } finally { setSubmitting(false); }
  };

  const statusColor = (s) => {
    const m = { submitted: 'bg-blue-100 text-blue-700', approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700', in_review: 'bg-amber-100 text-amber-700' };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-[#1B2A4A]">Student Nominations</h3>
          <p className="text-sm text-slate-500">Nominate students from your institution for IMCAC examinations</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm" data-testid="nominate-student-btn">
          <Users className="h-4 w-4 mr-2" /> Nominate Student
        </Button>
      </div>

      {/* Nomination Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" /> Nominate Student for Examination
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-[#C5A572]/10 p-3 rounded-sm border border-[#C5A572]/30 mb-4">
                  <p className="text-sm text-[#1B2A4A]"><strong>Nominating from:</strong> {institutionName || 'Your Institution'}</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Full Name *</Label>
                    <Input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="mt-1 rounded-sm" required />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 rounded-sm" required />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Date of Birth *</Label>
                    <Input type="date" value={form.date_of_birth} onChange={e => setForm({...form, date_of_birth: e.target.value})} className="mt-1 rounded-sm" required />
                  </div>
                  <div>
                    <Label className="text-xs">Gender</Label>
                    <Select value={form.gender} onValueChange={v => setForm({...form, gender: v})}>
                      <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select gender" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nationality</Label>
                    <Input value={form.nationality} onChange={e => setForm({...form, nationality: e.target.value})} className="mt-1 rounded-sm" />
                  </div>
                  <div>
                    <Label className="text-xs">Qualification</Label>
                    <Input value={form.qualification} onChange={e => setForm({...form, qualification: e.target.value})} className="mt-1 rounded-sm" placeholder="MBBS, BDS, etc." />
                  </div>
                  <div>
                    <Label className="text-xs">Medical Reg. Number</Label>
                    <Input value={form.medical_reg_number} onChange={e => setForm({...form, medical_reg_number: e.target.value})} className="mt-1 rounded-sm" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Examination *</Label>
                    <Select value={form.examination} onValueChange={v => setForm({...form, examination: v})}>
                      <SelectTrigger className="mt-1 rounded-sm"><SelectValue placeholder="Select examination" /></SelectTrigger>
                      <SelectContent>
                        {examinations.map(exam => (
                          <SelectItem key={exam.value} value={exam.value}>{exam.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Route</Label>
                    <Select value={form.route} onValueChange={v => setForm({...form, route: v})}>
                      <SelectTrigger className="mt-1 rounded-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Route 1 - Standard</SelectItem>
                        <SelectItem value="2">Route 2 - Experience</SelectItem>
                        <SelectItem value="3">Route 3 - International</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Years of Practice</Label>
                    <Input type="number" value={form.years_of_practice} onChange={e => setForm({...form, years_of_practice: e.target.value})} className="mt-1 rounded-sm" min="0" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Specialty (if applicable)</Label>
                    <Input value={form.specialty} onChange={e => setForm({...form, specialty: e.target.value})} className="mt-1 rounded-sm" placeholder="e.g., Dermatology, Plastic Surgery" />
                  </div>
                </div>
                
                <div className="flex gap-2 justify-end pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-sm">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm">
                    {submitting ? 'Submitting...' : 'Submit Nomination'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Nominations List */}
      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading nominations...</p>
      ) : nominations.length === 0 ? (
        <Card className="bg-slate-50">
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No students nominated yet</p>
            <p className="text-sm text-slate-400 mt-1">Click "Nominate Student" to submit your first nomination</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Card className="bg-blue-50">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-blue-700">{nominations.length}</p>
                <p className="text-xs text-blue-600">Total Nominated</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-amber-700">{nominations.filter(n => n.status === 'submitted').length}</p>
                <p className="text-xs text-amber-600">Pending Approval</p>
              </CardContent>
            </Card>
            <Card className="bg-green-50">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-green-700">{nominations.filter(n => n.status === 'approved' || n.allocated_center).length}</p>
                <p className="text-xs text-green-600">Approved/Allocated</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-purple-700">{nominations.filter(n => n.payment_status === 'paid').length}</p>
                <p className="text-xs text-purple-600">Fees Paid</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-50">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold text-slate-700">{nominations.filter(n => n.payment_status !== 'paid' && (n.status === 'approved' || n.allocated_center)).length}</p>
                <p className="text-xs text-slate-600">Payment Pending</p>
              </CardContent>
            </Card>
          </div>

          {/* 20% Discount Banner */}
          <div className="bg-gradient-to-r from-[#C5A572] to-[#D4B896] p-4 rounded-lg text-white mb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Institutional Discount Applied!</p>
                <p className="text-sm opacity-90">All student nominations get 20% discount on examination fees</p>
              </div>
              <Badge className="bg-white text-[#C5A572] text-lg px-3 py-1">20% OFF</Badge>
            </div>
          </div>

          {/* Nominations Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F8FAFC]">
                  <TableHead className="text-xs font-bold">Student</TableHead>
                  <TableHead className="text-xs font-bold">Examination</TableHead>
                  <TableHead className="text-xs font-bold">Exam Center</TableHead>
                  <TableHead className="text-xs font-bold">Exam Date</TableHead>
                  <TableHead className="text-xs font-bold">Status</TableHead>
                  <TableHead className="text-xs font-bold">Fee (20% off)</TableHead>
                  <TableHead className="text-xs font-bold">Payment</TableHead>
                  <TableHead className="text-xs font-bold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {nominations.map(nom => (
                  <TableRow key={nom.id} className={nom.payment_status === 'paid' ? 'bg-green-50/50' : ''}>
                    <TableCell>
                      <p className="font-medium text-sm">{nom.full_name}</p>
                      <p className="text-xs text-slate-500">{nom.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">{nom.examination}</Badge>
                      <p className="text-xs text-slate-400 mt-1">Route {nom.route}</p>
                    </TableCell>
                    <TableCell>
                      {nom.allocated_center ? (
                        <div>
                          <p className="text-sm font-medium text-green-700">{nom.allocated_center}</p>
                          <p className="text-xs text-slate-500">{nom.allocated_center_city}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">Pending allocation</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {nom.exam_date ? (
                        <div>
                          <p className="text-sm">{new Date(nom.exam_date).toLocaleDateString()}</p>
                          <p className="text-xs text-slate-500">{nom.exam_time}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">TBD</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-xs ${statusColor(nom.status)}`}>
                        {nom.status === 'approved' && nom.allocated_center ? 'Allocated' : nom.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="text-xs text-slate-400 line-through">₹{getExamFee(nom.examination, feeStructure).toLocaleString()}</p>
                        <p className="text-sm font-semibold text-green-700">₹{Math.round(getExamFee(nom.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION)).toLocaleString()}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {nom.payment_status === 'paid' ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                      ) : nom.payment_status === 'pending' ? (
                        <Badge className="bg-amber-100 text-amber-700 text-xs">Processing</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600 text-xs">Unpaid</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {nom.payment_status !== 'paid' && (nom.status === 'approved' || nom.allocated_center) ? (
                        <Button 
                          size="sm" 
                          className="bg-[#C5A572] hover:bg-[#B8956A] text-white text-xs rounded-sm h-7"
                          onClick={() => handlePayFee(nom)}
                        >
                          <CreditCard className="h-3 w-3 mr-1" /> Pay Fee
                        </Button>
                      ) : nom.payment_status === 'paid' ? (
                        <Badge className="bg-green-100 text-green-700 text-xs">Paid</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">Awaiting approval</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedNomination && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader className="bg-[#1B2A4A] text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" /> Pay Examination Fee
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-600">Student: <strong>{selectedNomination.full_name}</strong></p>
                  <p className="text-sm text-slate-600">Examination: <strong>{selectedNomination.examination}</strong></p>
                  {selectedNomination.allocated_center && (
                    <p className="text-sm text-slate-600">Center: <strong>{selectedNomination.allocated_center}</strong></p>
                  )}
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Standard Fee ({selectedNomination.examination}):</span>
                    <span className="line-through text-slate-400">₹{getExamFee(selectedNomination.examination, feeStructure).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-green-600">Institutional Discount ({Math.round(DISCOUNT_RATES.INSTITUTION * 100)}%):</span>
                    <span className="text-green-600">-₹{Math.round(getExamFee(selectedNomination.examination, feeStructure) * DISCOUNT_RATES.INSTITUTION).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total Payable:</span>
                    <span className="text-[#C5A572]">₹{Math.round(getExamFee(selectedNomination.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION)).toLocaleString()}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => { setShowPaymentModal(false); setSelectedNomination(null); }} className="flex-1 rounded-sm">
                    Cancel
                  </Button>
                  <PaymentButton
                    amount={Math.round(getExamFee(selectedNomination.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION))}
                    description={`${selectedNomination.examination} Exam Fee - ${selectedNomination.full_name}`}
                    onSuccess={(paymentId) => handlePaymentSuccess(paymentId, selectedNomination)}
                    onError={(error) => toast.error(error || 'Payment failed')}
                    className="flex-1 bg-[#22c55e] hover:bg-[#16a34a] text-white rounded-sm"
                  >
                    Pay ₹{Math.round(getExamFee(selectedNomination.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION)).toLocaleString()}
                  </PaymentButton>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default function AccreditationDashboard() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feeStructure, setFeeStructure] = useState(null);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [selectedAppId, setSelectedAppId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [studentResults, setStudentResults] = useState(null);
  const [nominations, setNominations] = useState([]);
  const [applicationForm, setApplicationForm] = useState({
    institution_name: '',
    institution_type: '',
    registration_number: '',
    country: '',
    city: '',
    address: '',
    contact_person: '',
    designation: '',
    email: '',
    phone: '',
    program_name: '',
    program_type: '',
    program_duration: '',
    student_capacity: 0,
    faculty_count: 0,
    accreditation_purpose: '',
    declaration: false
  });

  useEffect(() => {
    if (token) {
      fetchDashboard();
      loadFeeStructure();
      fetchNominationsForFees();
    }
  }, [token]);

  const fetchNominationsForFees = async () => {
    try {
      const res = await axios.get(`${API}/accreditation/student-nominations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNominations(res.data);
    } catch (err) { console.error(err); }
  };

  // Auto-fetch results when dashboard loads and there are approved apps
  useEffect(() => {
    if (dashboard?.applications) {
      const approved = dashboard.applications.filter(a => a.status === 'approved');
      if (approved.length > 0 && !studentResults) {
        fetchStudentResults(approved[0].id);
      }
    }
  }, [dashboard]);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/accreditation/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboard(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadFeeStructure = async () => {
    try {
      const fees = await fetchFeeStructure();
      setFeeStructure(fees);
    } catch (err) { 
      console.error(err); 
      // Use default fees on error
      setFeeStructure(getDefaultFees());
    }
  };

  const fetchStudentResults = async (appId) => {
    try {
      const res = await axios.get(`${API}/accreditation/student-results/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentResults(res.data);
    } catch (err) {
      toast.error('Failed to fetch student results');
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    if (!applicationForm.declaration) {
      toast.error('Please accept the declaration');
      return;
    }
    try {
      const res = await axios.post(`${API}/accreditation/apply`, applicationForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Application submitted successfully');
      setShowApplyDialog(false);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    }
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file || !selectedAppId) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const endpoint = type === 'document' 
        ? `${API}/accreditation/upload-document?app_id=${selectedAppId}&document_type=supporting`
        : `${API}/accreditation/upload-student-list?app_id=${selectedAppId}`;
      
      await axios.post(endpoint, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${type === 'document' ? 'Document' : 'Student list'} uploaded successfully`);
      setShowUploadDialog(false);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const updatePayment = async (appId, status) => {
    try {
      await axios.put(`${API}/accreditation/application/${appId}/payment?payment_status=${status}&payment_method=online`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Payment status updated');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to update payment');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Submitted' },
      under_review: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Under Review' },
      approved: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
      rejected: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' },
      pending: { bg: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Pending' }
    };
    const style = styles[status] || styles.pending;
    const Icon = style.icon;
    return (
      <Badge className={`${style.bg} rounded-sm px-2 py-0.5`}>
        <Icon className="h-3 w-3 mr-1" /> {style.label}
      </Badge>
    );
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading...</div>;
  }

  const stats = dashboard?.stats || {};
  const applications = dashboard?.applications || [];
  const approvedApps = applications.filter(a => a.status === 'approved');

  // Prepare chart data for results
  const resultsChartData = studentResults ? [
    { name: 'Passed', value: studentResults.results?.passed || 0, color: '#22c55e' },
    { name: 'Failed', value: studentResults.results?.failed || 0, color: '#ef4444' },
    { name: 'Pending', value: studentResults.results?.pending || 0, color: '#f59e0b' }
  ] : [];

  return (
    <div className="bg-[#F8FAFC] min-h-screen" data-testid="accreditation-dashboard">
      {/* Header */}
      <section className="bg-[#1B2A4A] py-10">
        <div className="imcac-container">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-[#C5A572] mb-2">Accreditation Portal</p>
              <h1 className="text-3xl font-bold text-white font-heading">Welcome, {user?.full_name}</h1>
              <p className="text-sm text-slate-300 mt-1">Manage institutional accreditation with 20% discount</p>
            </div>
            <Button 
              onClick={() => setShowApplyDialog(true)}
              className="bg-[#C5A572] text-white rounded-sm"
              data-testid="new-application-btn"
            >
              <FileText className="h-4 w-4 mr-1" /> New Application
            </Button>
          </div>
        </div>
      </section>

      <div className="imcac-container py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-blue-100 text-blue-600">
                <FileText className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{stats.total_applications || 0}</p>
                <p className="text-xs text-slate-500">Applications</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-green-100 text-green-600">
                <CheckCircle className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{stats.approved || 0}</p>
                <p className="text-xs text-slate-500">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-amber-100 text-amber-600">
                <Clock className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{stats.pending || 0}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-purple-100 text-purple-600">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1B2A4A]">{stats.student_uploads || 0}</p>
                <p className="text-xs text-slate-500">Student Uploads</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-emerald-100 text-emerald-600">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <p className="text-lg font-bold text-[#1B2A4A]">₹{(stats.total_fees_paid || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-500">Fees Paid</p>
              </div>
            </CardContent>
          </Card>
          <Card className="imcac-card border-l-4 border-l-[#C5A572]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm flex items-center justify-center bg-[#C5A572]/20 text-[#C5A572]">
                <Percent className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#C5A572]">20%</p>
                <p className="text-xs text-slate-500">Discount</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Card className="imcac-card">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="w-full justify-start h-auto flex-wrap bg-[#F8FAFC] rounded-none border-b border-slate-200 p-1 gap-1">
              <TabsTrigger value="overview" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <BarChart2 className="h-4 w-4 mr-1" /> Overview
              </TabsTrigger>
              <TabsTrigger value="applications" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-applications">
                <FileText className="h-4 w-4 mr-1" /> Applications
              </TabsTrigger>
              <TabsTrigger value="nominate" className="rounded-sm text-sm data-[state=active]:bg-[#22c55e] data-[state=active]:text-white" data-testid="tab-nominate">
                <Users className="h-4 w-4 mr-1" /> Nominate Students
              </TabsTrigger>
              <TabsTrigger value="fees" className="rounded-sm text-sm data-[state=active]:bg-[#C5A572] data-[state=active]:text-white" data-testid="tab-fees">
                <CreditCard className="h-4 w-4 mr-1" /> Fees & Payments
              </TabsTrigger>
              <TabsTrigger value="results" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white" data-testid="tab-results">
                <TrendingUp className="h-4 w-4 mr-1" /> Results
              </TabsTrigger>
              <TabsTrigger value="messages" className="rounded-sm text-sm data-[state=active]:bg-[#1B2A4A] data-[state=active]:text-white">
                <MessageSquare className="h-4 w-4 mr-1" /> Messages
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="p-5">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Fee Structure Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Percent className="h-4 w-4 text-[#C5A572]" /> 20% Discount Fee Structure
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {feeStructure && (
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-slate-600">Standard Fee</span>
                          <span className="text-sm line-through text-slate-400">₹{feeStructure.standard_fee?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-[#1B2A4A]">Your Discounted Fee</span>
                          <span className="text-lg font-bold text-green-600">₹{feeStructure.discounted_fee?.toLocaleString()}</span>
                        </div>
                        <Separator />
                        <p className="text-xs text-slate-500">{feeStructure.discount_note}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Button 
                      onClick={() => setShowApplyDialog(true)} 
                      className="w-full justify-start bg-[#1B2A4A] text-white rounded-sm"
                    >
                      <FileText className="h-4 w-4 mr-2" /> Submit New Application
                    </Button>
                    {approvedApps.length > 0 && (
                      <>
                        <Button 
                          onClick={() => { setSelectedAppId(approvedApps[0]?.id); setShowUploadDialog(true); }}
                          variant="outline"
                          className="w-full justify-start rounded-sm"
                        >
                          <Upload className="h-4 w-4 mr-2" /> Upload Student List
                        </Button>
                        <Button 
                          onClick={() => { fetchStudentResults(approvedApps[0]?.id); setActiveTab('results'); }}
                          variant="outline"
                          className="w-full justify-start rounded-sm"
                        >
                          <TrendingUp className="h-4 w-4 mr-2" /> View Student Results
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Recent Applications */}
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-base">Recent Applications</CardTitle>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                      <p>No applications yet. Start your accreditation journey!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {applications.slice(0, 3).map(app => (
                        <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                          <div>
                            <p className="font-medium text-[#1B2A4A]">{app.institution_name}</p>
                            <p className="text-xs text-slate-500">{app.program_name}</p>
                          </div>
                          {getStatusBadge(app.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Applications Tab */}
            <TabsContent value="applications" className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-[#1B2A4A]">Your Applications</h3>
                <Button onClick={() => setShowApplyDialog(true)} className="bg-[#1B2A4A] text-white rounded-sm text-sm">
                  <FileText className="h-4 w-4 mr-1" /> New Application
                </Button>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Building2 className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>No applications found</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {applications.map(app => (
                    <Card key={app.id} className="hover:border-[#C5A572] transition-colors">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-[#1B2A4A]">{app.institution_name}</h4>
                              {getStatusBadge(app.status)}
                            </div>
                            <p className="text-sm text-slate-600">{app.program_name} • {app.program_type}</p>
                            <p className="text-xs text-slate-500 mt-1">{app.city}, {app.country}</p>
                            <div className="flex gap-4 mt-2 text-xs text-slate-500">
                              <span>Capacity: {app.student_capacity} students</span>
                              <span>Faculty: {app.faculty_count}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-500">Submitted</p>
                            <p className="text-sm text-slate-700">{new Date(app.created_at).toLocaleDateString()}</p>
                            {app.fee_status === 'pending' && app.status !== 'rejected' && (
                              <Badge className="mt-2 bg-amber-100 text-amber-700 text-xs">Fee Pending</Badge>
                            )}
                            {app.fee_status === 'completed' && (
                              <Badge className="mt-2 bg-green-100 text-green-700 text-xs">Fee Paid</Badge>
                            )}
                          </div>
                        </div>
                        {app.status === 'approved' && (
                          <div className="mt-3 pt-3 border-t flex gap-2">
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="rounded-sm text-xs"
                              onClick={() => { fetchStudentResults(app.id); setActiveTab('results'); }}
                            >
                              <Eye className="h-3 w-3 mr-1" /> View Results
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Nominate Students Tab */}
            <TabsContent value="nominate" className="p-5" data-testid="nominate-content">
              <StudentNominationTab token={token} institutionName={dashboard?.accreditation?.institution_name} />
            </TabsContent>

            {/* Fees & Payments Tab */}
            <TabsContent value="fees" className="p-5" data-testid="fees-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Fees & Payments</h3>
              
              {/* Discount Banner */}
              <Card className="mb-6 bg-gradient-to-r from-[#C5A572]/10 to-[#1B2A4A]/10 border-[#C5A572]">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-[#C5A572] text-white">
                    <span className="text-xl font-bold">20%</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1B2A4A]">Exclusive Portal Discount</h4>
                    <p className="text-sm text-slate-600">All fees through this portal include 20% institutional discount</p>
                  </div>
                </CardContent>
              </Card>

              {/* Accreditation Fee Payments - Institution specific, kept separate */}
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="text-base">Accreditation Fee</CardTitle>
                </CardHeader>
                <CardContent>
                  {applications.length === 0 ? (
                    <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-sm">
                      <FileText className="h-5 w-5 text-slate-400" />
                      <div>
                        <p className="text-sm text-slate-600">No accreditation applications found</p>
                        <p className="text-xs text-slate-400 mt-1">Submit an accreditation application from the Applications tab to see fees here</p>
                      </div>
                    </div>
                  ) : applications.filter(a => a.fee_status === 'pending' && a.status !== 'rejected').length === 0 ? (
                    applications.filter(a => a.fee_status === 'completed').length > 0 ? (
                      <div className="flex items-center gap-3 p-4 bg-green-50 rounded-sm">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <p className="text-sm text-green-700">All accreditation fees have been paid</p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-4 bg-amber-50 rounded-sm">
                        <Clock className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="text-sm text-amber-700">Application pending approval</p>
                          <p className="text-xs text-amber-600 mt-1">Fee payment will be available after your application is approved</p>
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="space-y-3">
                      {applications.filter(a => a.fee_status === 'pending' && a.status !== 'rejected').map(app => (
                        <div key={app.id} className="flex items-center justify-between p-4 border rounded-sm">
                          <div>
                            <p className="font-medium text-[#1B2A4A]">{app.institution_name}</p>
                            <p className="text-sm text-slate-500">{app.program_name}</p>
                            <p className="text-xs text-slate-400 mt-1">Accreditation Fee</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-slate-400 line-through">₹1,50,000</p>
                            <p className="font-bold text-[#1B2A4A]">₹{(app.discounted_fee || 120000)?.toLocaleString()}</p>
                            <PaymentButton
                              amount={app.discounted_fee || 120000}
                              description={`Accreditation Fee - ${app.institution_name}`}
                              onSuccess={() => { toast.success('Payment successful!'); fetchDashboard(); }}
                              onError={(err) => toast.error(err || 'Payment failed')}
                              className="mt-2 bg-[#C5A572] hover:bg-[#B8956A] text-white text-xs rounded-sm"
                            >
                              Pay ₹{(app.discounted_fee || 120000)?.toLocaleString()}
                            </PaymentButton>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Student Examination Fees - Using shared FeesPaymentsSection component */}
              <FeesPaymentsSection
                token={token}
                pendingPayments={nominations
                  .filter(n => n.payment_status !== 'paid' && n.payment_status !== 'pending_verification' && (n.status === 'approved' || n.status === 'scheduled' || n.allocated_center))
                  .map(n => ({
                    ...n,
                    base_fee: getExamFee(n.examination, feeStructure),
                    fee: Math.round(getExamFee(n.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION))
                  }))
                }
                pendingVerification={nominations
                  .filter(n => n.payment_status === 'pending_verification')
                  .map(n => ({
                    ...n,
                    base_fee: Math.round(getExamFee(n.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION)),
                    bank_transfer_reference: n.bank_transfer?.reference
                  }))
                }
                onPaymentSuccess={async (paymentId, item) => {
                  try {
                    const discountedFee = Math.round(getExamFee(item.examination, feeStructure) * (1 - DISCOUNT_RATES.INSTITUTION));
                    const res = await axios.post(`${API}/accreditation/student-nominations/${item.id}/payment`, {
                      payment_id: paymentId,
                      amount: discountedFee
                    }, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (res.data.account_created) {
                      toast.success(`Payment successful! Candidate account created for ${item.full_name}. Login credentials sent to ${item.email}`, {
                        duration: 8000
                      });
                    } else {
                      toast.success('Payment successful! Receipt sent to student.');
                    }
                    fetchNominationsForFees();
                  } catch (err) {
                    toast.error('Payment recorded but update failed. Contact support.');
                  }
                }}
                onBankTransferSubmit={async (itemId, bankTransferData) => {
                  await axios.post(`${API}/accreditation/student-nominations/${itemId}/bank-transfer`, bankTransferData, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  fetchNominationsForFees();
                }}
                portalType="accreditation"
                discountRate={DISCOUNT_RATES.INSTITUTION}
                getExamFee={getExamFee}
                feeStructure={feeStructure}
              />
            </TabsContent>

            {/* Results Tab */}
            <TabsContent value="results" className="p-5" data-testid="results-content">
              <h3 className="text-lg font-semibold text-[#1B2A4A] mb-4">Student Results</h3>
              
              {approvedApps.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p>Results will be available once you have approved accreditation</p>
                </div>
              ) : (
                <>
                  {studentResults ? (
                    <>
                      <div className="grid md:grid-cols-2 gap-6">
                      {/* Stats */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">{studentResults.institution_name}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="text-center p-3 bg-blue-50 rounded-sm">
                              <p className="text-2xl font-bold text-blue-600">{studentResults.total_students}</p>
                              <p className="text-xs text-slate-500">Total Students</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded-sm">
                              <p className="text-2xl font-bold text-green-600">{studentResults.results?.pass_rate || 0}%</p>
                              <p className="text-xs text-slate-500">Pass Rate</p>
                            </div>
                            <div className="text-center p-3 bg-emerald-50 rounded-sm">
                              <p className="text-2xl font-bold text-emerald-600">{studentResults.results?.passed || 0}</p>
                              <p className="text-xs text-slate-500">Passed</p>
                            </div>
                            <div className="text-center p-3 bg-red-50 rounded-sm">
                              <p className="text-2xl font-bold text-red-600">{studentResults.results?.failed || 0}</p>
                              <p className="text-xs text-slate-500">Failed</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Chart */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="text-base">Results Distribution</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-48">
                            <ResponsiveContainer width="100%" height="100%">
                              <RePieChart>
                                <Pie
                                  data={resultsChartData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={40}
                                  outerRadius={60}
                                  dataKey="value"
                                  label={({ name, value }) => `${name}: ${value}`}
                                >
                                  {resultsChartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip />
                              </RePieChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Detailed Results Table */}
                    {studentResults.detailed_results && studentResults.detailed_results.length > 0 && (
                      <Card className="mt-4">
                        <CardHeader>
                          <CardTitle className="text-base">Nominated Students - Detailed Results</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-[#F8FAFC]">
                                  <TableHead className="text-xs font-bold">Student Name</TableHead>
                                  <TableHead className="text-xs font-bold">Examination</TableHead>
                                  <TableHead className="text-xs font-bold">Exam Center</TableHead>
                                  <TableHead className="text-xs font-bold">Exam Date</TableHead>
                                  <TableHead className="text-xs font-bold">Score</TableHead>
                                  <TableHead className="text-xs font-bold">Result</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {studentResults.detailed_results.map((result, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell>
                                      <p className="font-medium text-sm">{result.student_name}</p>
                                      <p className="text-xs text-slate-500">{result.email}</p>
                                    </TableCell>
                                    <TableCell><Badge variant="outline" className="text-xs">{result.examination}</Badge></TableCell>
                                    <TableCell className="text-sm">{result.allocated_center || '-'}</TableCell>
                                    <TableCell className="text-sm">{result.exam_date ? new Date(result.exam_date).toLocaleDateString() : '-'}</TableCell>
                                    <TableCell className="text-sm font-medium">{result.total_score ? `${result.total_score}%` : '-'}</TableCell>
                                    <TableCell>
                                      {result.final_result === 'Pass' ? (
                                        <Badge className="bg-green-100 text-green-700 text-xs">Pass</Badge>
                                      ) : result.final_result === 'Fail' ? (
                                        <Badge className="bg-red-100 text-red-700 text-xs">Fail</Badge>
                                      ) : (
                                        <Badge className="bg-amber-100 text-amber-700 text-xs">Pending</Badge>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                    </>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      <TrendingUp className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>Loading results for your nominated students...</p>
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Messages Tab */}
            <TabsContent value="messages" className="p-5">
              <MessagesTab token={token} portalType="accreditation" portalName="Accreditation" />
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* New Application Dialog */}
      <Dialog open={showApplyDialog} onOpenChange={setShowApplyDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Accreditation Application</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitApplication} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Institution Name *</Label>
                <Input 
                  value={applicationForm.institution_name}
                  onChange={e => setApplicationForm({...applicationForm, institution_name: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Institution Type *</Label>
                <Select onValueChange={v => setApplicationForm({...applicationForm, institution_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teaching_hospital">Teaching Hospital</SelectItem>
                    <SelectItem value="medical_college">Medical College</SelectItem>
                    <SelectItem value="training_center">Training Center</SelectItem>
                    <SelectItem value="clinic">Clinic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Registration Number</Label>
                <Input 
                  value={applicationForm.registration_number}
                  onChange={e => setApplicationForm({...applicationForm, registration_number: e.target.value})}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Country *</Label>
                <Input 
                  value={applicationForm.country}
                  onChange={e => setApplicationForm({...applicationForm, country: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">City *</Label>
                <Input 
                  value={applicationForm.city}
                  onChange={e => setApplicationForm({...applicationForm, city: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Contact Person *</Label>
                <Input 
                  value={applicationForm.contact_person}
                  onChange={e => setApplicationForm({...applicationForm, contact_person: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Designation</Label>
                <Input 
                  value={applicationForm.designation}
                  onChange={e => setApplicationForm({...applicationForm, designation: e.target.value})}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Email *</Label>
                <Input 
                  type="email"
                  value={applicationForm.email}
                  onChange={e => setApplicationForm({...applicationForm, email: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Phone *</Label>
                <Input 
                  value={applicationForm.phone}
                  onChange={e => setApplicationForm({...applicationForm, phone: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Program Name *</Label>
                <Input 
                  value={applicationForm.program_name}
                  onChange={e => setApplicationForm({...applicationForm, program_name: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-xs">Program Type *</Label>
                <Select onValueChange={v => setApplicationForm({...applicationForm, program_type: v})}>
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residency">Residency</SelectItem>
                    <SelectItem value="fellowship">Fellowship</SelectItem>
                    <SelectItem value="training_program">Training Program</SelectItem>
                    <SelectItem value="cpd">CPD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Program Duration</Label>
                <Input 
                  value={applicationForm.program_duration}
                  onChange={e => setApplicationForm({...applicationForm, program_duration: e.target.value})}
                  className="mt-1 rounded-sm"
                  placeholder="e.g., 12 months"
                />
              </div>
              <div>
                <Label className="text-xs">Student Capacity</Label>
                <Input 
                  type="number"
                  value={applicationForm.student_capacity}
                  onChange={e => setApplicationForm({...applicationForm, student_capacity: parseInt(e.target.value) || 0})}
                  className="mt-1 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Faculty Count</Label>
                <Input 
                  type="number"
                  value={applicationForm.faculty_count}
                  onChange={e => setApplicationForm({...applicationForm, faculty_count: parseInt(e.target.value) || 0})}
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address *</Label>
              <Input 
                value={applicationForm.address}
                onChange={e => setApplicationForm({...applicationForm, address: e.target.value})}
                className="mt-1 rounded-sm"
                required
              />
            </div>
            <div>
              <Label className="text-xs">Accreditation Purpose *</Label>
              <textarea 
                value={applicationForm.accreditation_purpose}
                onChange={e => setApplicationForm({...applicationForm, accreditation_purpose: e.target.value})}
                className="mt-1 w-full p-2 border rounded-sm text-sm"
                rows={3}
                required
              />
            </div>

            {/* Fee Display */}
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-3 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-green-800">20% Discount Applied</p>
                  <p className="text-xs text-green-600">Standard: ₹1,50,000 → Your Fee: ₹1,20,000</p>
                </div>
                <Badge className="bg-green-600 text-white">Save ₹30,000</Badge>
              </CardContent>
            </Card>

            <div className="flex items-start gap-2">
              <input 
                type="checkbox"
                checked={applicationForm.declaration}
                onChange={e => setApplicationForm({...applicationForm, declaration: e.target.checked})}
                className="mt-1"
              />
              <Label className="text-xs text-slate-600">
                I declare that all information provided is accurate and I agree to IMCAC's terms and conditions for accreditation.
              </Label>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowApplyDialog(false)} className="rounded-sm">Cancel</Button>
              <Button type="submit" className="bg-[#1B2A4A] text-white rounded-sm">Submit Application</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Student List</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Upload a CSV or Excel file containing student information for your accredited institution.</p>
            <div className="border-2 border-dashed rounded-sm p-8 text-center">
              <Upload className="h-8 w-8 mx-auto mb-2 text-slate-400" />
              <p className="text-sm text-slate-600 mb-2">Drag and drop or click to upload</p>
              <input 
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileUpload(e, 'student_list')}
                className="hidden"
                id="student-upload"
              />
              <Button 
                variant="outline" 
                className="rounded-sm"
                disabled={uploading}
                onClick={() => document.getElementById('student-upload').click()}
              >
                {uploading ? 'Uploading...' : 'Select File'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
